import { capitalize } from 'lodash';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { EpisodeAsset } from '../../../../queries/ats/titles/types/asset';
import { Sources } from '../../../../queries/ats/titles/utils/sources-utils';
import { DetailsErrorBoundary } from '../../details-error/details-error';
import { Episode } from '../episode/episode';
import css from './date-based-episode-list.module.scss';

interface DateBasedEpisodeListProps {
  episodes: EpisodeAsset[];
}

export function DateBasedEpisodeList({ episodes }: DateBasedEpisodeListProps) {
  const sortedEpisodes = getSortedEpisodes(episodes);
  const startTimes = sortedEpisodes.map((episode) => getDate(episode));
  const nearestDate = findNearestDate(startTimes, moment());
  const indexOfNearestFutureDate = startTimes.indexOf(nearestDate);
  // Negative 2 to get the first 3 future episodes
  const relevantEpisodes = sortedEpisodes.slice(Math.max(indexOfNearestFutureDate - 2, 0), sortedEpisodes.length);

  const sections = relevantEpisodes.reduce((map, episode) => {
    const key = getDate(episode).format('YYYY-MM-DD');
    const dates = map.get(key) ?? [];

    if (isLiveEpisode(episode)) {
      dates.push(episode);
      return map.set(key, dates);
    }

    if (isBeforeLiveToday(episode)) {
      return map;
    }

    dates.push(episode);
    return map.set(key, dates);
  }, new Map<string, EpisodeAsset[]>());

  return (
    <div className={css.list}>
      {[...sections.entries()].map(([key, section]) => (
        <section key={key} className={css.list}>
          <DateSeparator time={moment(key)} />
          {section.map((episode) => (
            <DetailsErrorBoundary key={episode.masterId} label="episode">
              <Episode key={episode.masterId} episode={episode} />
            </DetailsErrorBoundary>
          ))}
        </section>
      ))}
    </div>
  );
}

interface DateSeparatorProps {
  time: moment.Moment;
}

function DateSeparator({ time }: DateSeparatorProps) {
  const { t } = useTranslation();

  const dateText = time.calendar({
    sameDay: `[${t('time:today')}] Do MMMM`,
    nextDay: `[${t('time:tomorrow')}] Do MMMM`,
    lastDay: `[${t('time:yesterday')}] Do MMMM`,
    nextWeek: 'dddd Do MMMM',
    lastWeek: 'dddd Do MMMM',
    sameElse: 'dddd Do MMMM',
  });

  return (
    <span className={css.thematicContainer}>
      <hr className={css.thematicBrakeStart} />
      <h3 className={css.dateText}>{capitalize(dateText)}</h3>
      <hr className={css.thematicBrakeEnd} />
    </span>
  );
}

function getSortedEpisodes(episodes: EpisodeAsset[]) {
  return episodes.sort((a, b) => {
    const aSource = new Sources(a.sources).first;
    const bSource = new Sources(b.sources).first;

    if (aSource.type === 'program' && bSource.type === 'program') {
      return new Date(bSource.startTime).valueOf() - new Date(aSource.startTime).valueOf();
    }

    // fallback to episode start time, should be the same in most cases but we should try to resolve playback option start time first.
    return new Date(b.nextStartTime ?? 0).valueOf() - new Date(a.nextStartTime ?? 0).valueOf();
  });
}

export function findNearestDate(dates: moment.Moment[], target: moment.Moment) {
  return dates.reduce((prev, curr) => {
    return curr.isAfter(target) ? curr : prev;
  });
}

function findDefaultNewsPlaybackOption(episode: EpisodeAsset) {
  const { sources } = episode;
  const selectedSource = new Sources(sources).first;
  if (selectedSource?.type !== 'program') {
    throw new Error(`Unexpected source type, got "${selectedSource?.type}", but expected "channel"`);
  }
  return selectedSource;
}

function getDate(episode: EpisodeAsset) {
  const playbackOption = findDefaultNewsPlaybackOption(episode);
  return moment(playbackOption.startTime);
}

function isLiveEpisode(episode: EpisodeAsset) {
  const playbackOption = findDefaultNewsPlaybackOption(episode);

  const now = moment();
  const startTime = moment(playbackOption.startTime);
  const endTime = moment(playbackOption.endTime);

  return now.isBetween(startTime, endTime);
}

function isBeforeLiveToday(episode: EpisodeAsset) {
  const playbackOption = findDefaultNewsPlaybackOption(episode);
  const now = moment();
  const startTime = moment(playbackOption.startTime);
  return startTime.isSame(now, 'd') && startTime.isBefore(now);
}
